/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useUserLocation } from 'shared-ui/geolocation';
import { getSearchResults, getSearchSuggestions } from './search.api';
import {
    transformSearchResult,
    transformSearchSuggestionResult,
} from './search.transforms';
import { useDebounce } from '../utils/useDebounce';

import { useFiltersState } from '../FiltersContext';

import { filtersToApiParams } from './filtersToApiParams/filtersToApiParams';
import { SearchResult } from './search.types';

export const SEARCH_RESULTS_KEY = 'searchResults';
export const SEARCH_RESULTS_PREVIEW_KEY = 'searchResultsPreview';
export const SEARCH_SUGGESTIONS_KEY = 'searchSuggestions';

export function useSearchSuggestionsQuery(query: string) {
    const debouncedQuery = useDebounce(query, 200);

    return useQuery({
        queryKey: [SEARCH_SUGGESTIONS_KEY, debouncedQuery],
        queryFn: async ({ signal }) => getSearchSuggestions(debouncedQuery, signal),
        staleTime: Infinity,
        retry: 1,
        enabled: !!debouncedQuery,
        placeholderData: keepPreviousData,
        select: transformSearchSuggestionResult,
    });
}

function useHasLocationForSearchResults() {
    const userLocation = useUserLocation();
    const [filters] = useFiltersState();

    const hasLocation = !!filters.location;
    const hasLatLng = !!(filters.lat && filters.lng);
    const hasBoundingBox = !!(
        filters.latSw &&
        filters.lngSw &&
        filters.latNe &&
        filters.lngNe
    );

    const hasLocationData =
        !!userLocation && (hasLocation || hasLatLng || hasBoundingBox);

    return hasLocationData;
}

export function useSearchResultsQuery() {
    const hasLocationData = useHasLocationForSearchResults();
    const [filters] = useFiltersState();

    const paramsString = hasLocationData ? filtersToApiParams(filters).toString() : '';
    const params = React.useMemo(
        () => (paramsString ? new URLSearchParams(paramsString) : new URLSearchParams()),
        [paramsString]
    );

    /** Stable selector: only runs when query data or locationId changes. */
    const select = React.useCallback(
        (data: SearchResult) => transformSearchResult(data, filters.locationId),
        [filters.locationId]
    );

    const query = useQuery({
        queryKey: [SEARCH_RESULTS_KEY, paramsString],
        queryFn: async ({ signal }) => getSearchResults(params, signal),
        staleTime: Infinity,
        retry: 3,
        // retryOnMount: false prevents an infinite retry loop on total API failure:
        // several mounted components share this query, and each new subscriber
        // would otherwise kick off another round of retries.
        retryOnMount: false,
        placeholderData: keepPreviousData,
        enabled: hasLocationData,
        select,
    });

    return query;
}
